import React, { memo, type PropsWithChildren } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { StyleSheet, View } from 'react-native';
import Animated, {
  FadeIn,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

import { theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPromptContentContainer = memo((props: Props) => {
  const { children, enteringDelay = 0, opacitySV } = props;

  const fallbackOpacitySV = useSharedValue(1);
  const effectiveOpacitySV = opacitySV ?? fallbackOpacitySV;

  const animatedStyles = useAnimatedStyle(() => ({
    opacity: effectiveOpacitySV.value,
  }));

  const containerStyles = [styles.container, animatedStyles];

  return (
    <Animated.View
      entering={FadeIn.duration(FADE_IN_DURATION).delay(enteringDelay)}
      style={containerStyles}
    >
      <LinearGradient colors={GRADIENT_COLORS} locations={GRADIENT_LOCATIONS}>
        <View style={styles.innerContainer}>{children}</View>
      </LinearGradient>
    </Animated.View>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_IN_DURATION = 600;

const GRADIENT_COLORS = [theme.colors.OPACITY.KALE.TRANSPARENT, theme.colors.KALE] as const;
const GRADIENT_LOCATIONS = [0, 0.5] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    marginTop: 'auto',
  },
  innerContainer: {
    paddingHorizontal: theme.spacing['8'],
    paddingVertical: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  enteringDelay?: number;
  opacitySV?: SharedValue<number>;
}>;
